import { Tooltip } from 'antd';
import { ModalProps } from 'antd/lib/modal';
import React, { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ProjectBaseline } from 'api/project';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Modal } from 'components/atoms/modal';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslation } from 'hooks/use-translation';
import { BaselineForm } from './form';

interface Props extends ModalProps {
  target: ProjectBaseline;
  onDeleteConfirm: (id) => void;
  baselines: ProjectBaseline[];
  onSubmit: (id, formValues) => void;
  isEditing: boolean;
  isDeleting: boolean;
}

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DeleteButton = styled(Button)`
  padding-left: 0 !important;
`;

const DisabledDeleteButton = () => {
  return (
    <Tooltip
      title={<FormattedMessage id="baseline.delete.disabled.tooltip" />}
      placement="bottom"
    >
      <div>
        <DeleteButton type="link" danger disabled>
          <FormattedMessage id="baseline.delete" />
        </DeleteButton>
      </div>
    </Tooltip>
  );
};

const Footer = ({
  isEditing,
  canDelete,
  onSubmit,
  onDelete,
  onCancel,
}: Pick<Props, 'isEditing' | 'isDeleting' | 'onCancel'> & {
  onDelete: () => void;
  onSubmit: () => void;
  canDelete: boolean;
}) => {
  return (
    <ButtonGroup>
      <div>
        {canDelete ? (
          <DeleteButton type="link" danger onClick={onDelete}>
            <FormattedMessage id="baseline.delete" />
          </DeleteButton>
        ) : (
          <DisabledDeleteButton />
        )}
      </div>
      <div>
        <Button type="link" onClick={onCancel}>
          <FormattedMessage id="cancel" />
        </Button>
        <Button loading={isEditing} type="primary" onClick={onSubmit}>
          <FormattedMessage id="save" />
        </Button>
      </div>
    </ButtonGroup>
  );
};

export const BaselineEditModal = ({
  target,
  baselines,
  onSubmit,
  isEditing,
  isDeleting,
  onDeleteConfirm,
  ...rest
}: Props) => {
  const [form] = Form.useForm();

  const DeleteConfirmation = useDeleteConfirmModal({
    title: useTranslation('baseline.delete.confirm.header'),
    content: useTranslation('baseline.delete.confirm.body'),
    onOk: () => onDeleteConfirm(target.baselineId),
    getContainer: rest.getContainer,
  });

  const onEdit = useCallback(async () => {
    const formValues = await form.validateFields();

    onSubmit(target.baselineId, formValues);
  }, [form, onSubmit, target.baselineId]);

  useEffect(() => {
    form.setFieldsValue({
      name: target.baselineName,
      notes: target.baselineNote,
    });
  }, [form, target.baselineName, target.baselineNote]);

  return (
    <Modal
      {...rest}
      zIndex={1051}
      centered
      visible
      footer={
        <Footer
          isEditing={isEditing}
          isDeleting={isDeleting}
          canDelete={!target.default}
          onSubmit={onEdit}
          onDelete={DeleteConfirmation}
          onCancel={rest.onCancel}
        />
      }
      title={<FormattedMessage id="baseline.edit" />}
      okText={<FormattedMessage id="save" />}
    >
      <BaselineForm
        form={form}
        baselines={baselines}
        currentBaseline={target}
      />
    </Modal>
  );
};
